/* eslint-disable import/no-extraneous-dependencies */

import { GridColDef } from '@mui/x-data-grid';
import { BrowseView, browseViewColumnAction, getMessageFromError } from '@pro4-erp/core-web';
import { Check as CheckIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import ReviewReminderModal from '../components/ReviewReminderModal';
import { reviewReminder } from '../api/reviewReminder';
import { getBeautifiedLocalDate } from '@/core';
import { Reminder, getRemindersForMe } from '@/common';

type ReviewReminderModalState =
  | { isOpen: true; reminder: Reminder }
  | { isOpen: false; reminder: undefined };

const BrowsePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [reviewReminderModal, setReviewReminderModal] = useState<ReviewReminderModalState>({
    isOpen: false,
    reminder: undefined,
  });

  const { mutate } = useMutation({
    mutationFn: reviewReminder,
    onSuccess: () => {
      // TODO: Replace this with browseView.refetch() once we have the hook
      queryClient.invalidateQueries(['does-user-have-any-reminders']);
      navigate(0);
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  const remindersColumns: GridColDef<Reminder>[] = [
    {
      ...browseViewColumnAction({
        field: 'review',
        headerName: t('Review', { defaultValue: 'Review' }).toString(),
        icon: {
          element: <CheckIcon />,
          color: 'primary',
        },
        onClick: ({ params }) =>
          setReviewReminderModal({ isOpen: true, reminder: params.row as Reminder }),
      }),
      display: 'flex',
    },
    {
      field: 'name',
      display: 'flex',
      headerName: t('Name', { defaultValue: 'Name' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'note',
      display: 'flex',
      headerName: t('Note', { defaultValue: 'Note' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'endType',
      display: 'flex',
      headerName: t('End type', { defaultValue: 'End type' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'remindDat',
      display: 'flex',
      headerName: t('Remind date', { defaultValue: 'Remind date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.remindDat ? getBeautifiedLocalDate(row.remindDat) : 'N/A';
      },
    },
    {
      field: 'remindEndDat',
      display: 'flex',
      headerName: t('Remind end date', { defaultValue: 'Remind end date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.remindEndDat ? getBeautifiedLocalDate(row.remindEndDat) : 'N/A';
      },
    },
  ];

  const reviewedRemindersColumns: GridColDef<Reminder>[] = [
    {
      field: 'name',
      display: 'flex',
      headerName: t('Name', { defaultValue: 'Name' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'note',
      display: 'flex',
      headerName: t('Note', { defaultValue: 'Note' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'endType',
      display: 'flex',
      headerName: t('End type', { defaultValue: 'End type' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'remindDat',
      display: 'flex',
      headerName: t('Remind date', { defaultValue: 'Remind date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.remindDat ? getBeautifiedLocalDate(row.remindDat) : 'N/A';
      },
    },
    {
      field: 'remindEndDat',
      display: 'flex',
      headerName: t('Remind end date', { defaultValue: 'Remind end date' }).toString(),
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      disableColumnMenu: true,
      valueGetter(_, row) {
        return row.remindEndDat ? getBeautifiedLocalDate(row.remindEndDat) : 'N/A';
      },
    },
  ];

  return (
    <>
      <ReviewReminderModal
        isOpen={reviewReminderModal.isOpen}
        setIsOpen={(isOpen) =>
          setReviewReminderModal((previous) => ({ ...previous, isOpen } as any))
        }
        reminder={reviewReminderModal.reminder}
        onSubmit={(data) => mutate({ id: reviewReminderModal.reminder?.id || '', data })}
      />
      <BrowseView
        title={<BrowseView.Title>{t('Reminders', { defaultValue: 'Reminders' })}</BrowseView.Title>}
        browserViewContainerSx={{
          height: '50vh',
        }}
        columns={remindersColumns}
        fetchData={(browseViewState) =>
          getRemindersForMe({ ...browseViewState, reminderType: 'ToBeReviewed' })
        }
        toolbar={<BrowseView.Toolbar showSearch />}
      />
      <Box sx={{ mt: '1rem' }} />
      <BrowseView
        title={
          <BrowseView.Title>
            {t('Reviewed Reminders', { defaultValue: 'Reviewed Reminders' })}
          </BrowseView.Title>
        }
        browserViewContainerSx={{
          height: '50vh',
        }}
        columns={reviewedRemindersColumns}
        fetchData={(browseViewState) =>
          getRemindersForMe({ ...browseViewState, reminderType: 'Ended' })
        }
        toolbar={<BrowseView.Toolbar showSearch />}
      />
    </>
  );
};

export default BrowsePage;
